/*  Shared  */
import { SendEmail } from 'application/Shared/Components/Buttons';
import {
    ManagedTableV2UI,
    useManagedTableV2Context
} from 'application/Shared/Components/Table';
import { Notify } from 'application/Shared/Helpers';
import { Confirm } from 'application/Shared/Modals';
import { debug } from 'application/Shared/Utils';

/* Hooks */
import { usePutUser } from 'hooks/dataAccess/User/user';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/* Internal */
import { AdminMergeUsersModal } from './AdminMergeUsersModal';
import { useAdminUsersTableContext } from './AdminUsersTableContext';

const ConfirmDisable = ({ disable, disabling, setDisabling }) => {
    const { t } = useTranslation();
    const { table } = useManagedTableV2Context();

    // disabling is the id that comes from the clicked row.
    let ids = [disabling];

    // Find out whether the disable button they clicked is part of a merge group or not.
    // If it does not, then ids is just the id of the clicked row.
    // If it does belong, then ids are the ids of that group that are not disabled already.
    const idsToConsider = [];
    if (table.getSelectedRowModel().rows.length) {
        // Filter out the already disabled users
        const idsToConsiderFiltered = table
            .getSelectedRowModel()
            .rows.filter((user) => user.original.disabled === false);
        idsToConsiderFiltered.forEach((user) => {
            idsToConsider.push(user.original.id);
        });
        if (idsToConsider.includes(disabling)) {
            ids = idsToConsider;
        }
    }

    return (
        <Confirm
            modalOpen={!!disabling}
            promptConfirmFn={() => disable({ ids })}
            promptCancelFn={() => {
                setDisabling(null);
            }}
            promptTitle={t('Users.confirm_disable.title')}
            promptAlertText={t('Users.confirm_disable.text')}
            promptAlertTextType="warning"
            promptText={t('Users.confirmDisable.subtitle', {
                count: ids.length || 1
            })}
        />
    );
};

const ConfirmEnable = ({ enable, enabling, setEnabling }) => {
    const { t } = useTranslation();

    return (
        <Confirm
            modalOpen={!!enabling}
            promptConfirmFn={() => enable({ id: enabling })}
            promptCancelFn={() => {
                setEnabling(null);
            }}
            promptTitle={t('Users.confirm_enable.title')}
            promptText={t('Users.confirm_enable.text')}
        />
    );
};

export const AdminUsersTable = ({ setResetRowSelection }) => {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const {
        disabling,
        enabling,
        openMergeModal,
        openSendEmail,
        setDisabling,
        setEnabling,
        setOpenMergeModal,
        setOpenSendEmail
    } = useAdminUsersTableContext();
    const { bulkActions, setRerender } = useManagedTableV2Context();

    const { putUser } = usePutUser();

    const csvFields = useMemo(
        () => [
            { key: 'id', label: t('Common.id') },
            { key: 'name', label: t('Common.name') },
            { key: 'email', label: t('Common.email') },
            { key: 'title', label: t('Common.title') },
            { key: 'lastCheckIn', label: t('Common.lastCheckIn') },
            { key: 'lastSignIn', label: t('Common.userLastSignIn') },
            { key: 'createdBy', label: t('Common.created_by') },
            {
                key: 'managerPlatformUserId',
                label: t('Common.manager')
            },
            {
                key: 'aliasesList',
                label: t('Common.aliases'),
                rowKeyAndDataPath: 'aliases',
                transform: 'listExtract'
            },
            { key: 'dateCreated', label: t('Common.userDateCreated') },
            {
                key: 'browsersList',
                label: t('Common.browsers'),
                rowKeyAndDataPath: 'browsers:application.name',
                transform: 'listExtract'
            }
        ],
        [t]
    );

    const onCloseMergeModal = () => {
        setOpenMergeModal(false);
        setResetRowSelection(true);
    };

    const disable = async ({ ids }) => {
        const params = { disabled: true };
        if (clientId) {
            params.platformTenantId = clientId;
        }

        try {
            await Promise.all(
                ids.map(async (id) => {
                    await putUser({ id, params });
                })
            );
            setRerender(true);
            Notify.s(t('Users.disable.success', { count: ids.length }));
        } catch {
            Notify.e(t('Users.disable.fail', { count: ids.length }));
        }
        setResetRowSelection(true);
        setDisabling(null);
    };

    const enable = async ({ id }) => {
        const params = { disabled: false };
        if (clientId) {
            params.platformTenantId = clientId;
        }

        try {
            const response = await putUser({ id, params });
            setRerender(true);
            if (response) {
                Notify.s(t('Users.enable.success'));
            } else {
                Notify.e(t('Users.enable.fail'));
            }
        } catch (e) {
            debug.e('Unable to enable user', e);
            Notify.e(t('Users.enable.fail'));
        }

        setEnabling(null);
    };

    return (
        <>
            {disabling && (
                <ConfirmDisable
                    disable={disable}
                    disabling={disabling}
                    setDisabling={setDisabling}
                />
            )}
            {enabling && (
                <ConfirmEnable
                    enable={enable}
                    enabling={enabling}
                    setEnabling={setEnabling}
                />
            )}
            {openMergeModal && (
                <AdminMergeUsersModal
                    onCloseMergeModal={onCloseMergeModal}
                    openMergeModal={openMergeModal}
                />
            )}
            {openSendEmail && (
                <SendEmail
                    onClose={() => setOpenSendEmail(null)}
                    to={openSendEmail}
                />
            )}
            <ManagedTableV2UI
                bulkActions={bulkActions}
                csvExport
                csvExportFields={csvFields}
                csvExportFileName="users_list"
                renderFooter
                titleDef={{ titleKey: 'Common.usersWithCount' }}
            />
        </>
    );
};
